/* MissionMgmtMenu — dropdown (3-dot) button for mission management.
   Only rendered when the current user is the creator or a participant.
   Actions:
     - Modifica (creator only) → opens MissionEditSheet
     - Annulla missione (creator only) → POST /missions/v2/:mid/cancel
     - Abbandona missione (participant, not creator) → POST /missions/v2/:mid/leave

   The parent (MissionDetail) supplies onReload / onCancelled callbacks
   so this component stays presentation-only besides the API calls.
*/
import React, { useState } from "react";
import { toast } from "sonner";
import { MoreVertical, Edit3, Trash2, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { hapticTap } from "@/lib/haptics";

export default function MissionMgmtMenu({ mission, isCreator, isParticipant, onEdit, onReload, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canCancel = isCreator && mission.status !== "closed" && mission.status !== "cancelled" && mission.status !== "completed";
  const canLeave = isParticipant && !isCreator && mission.status !== "closed";
  const canEdit = isCreator && (mission.status === "open" || mission.status === "in_progress");

  const doCancel = async () => {
    if (!confirm("Annullare questa missione? Tutti i partecipanti verranno avvisati.")) return;
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/missions/v2/${mission.id}/cancel`);
      toast.success("Missione annullata");
      setOpen(false);
      onCancelled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile annullare");
    } finally { setBusy(false); }
  };

  const doLeave = async () => {
    if (!confirm("Vuoi davvero abbandonare questa missione?")) return;
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/missions/v2/${mission.id}/leave`);
      toast.success("Hai lasciato la missione");
      setOpen(false);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile abbandonare");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <button
        onClick={() => { hapticTap(); setOpen((o) => !o); }}
        data-testid="mission-mgmt-btn"
        aria-label="Gestisci missione"
        className="h-9 w-9 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center hover:bg-[#FAF7F0]"
      >
        <MoreVertical className="h-4 w-4 text-[#3A3A3A]" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className="absolute right-0 mt-2 w-56 rounded-2xl bg-white border border-[#F0EBE3] shadow-xl overflow-hidden z-20"
            data-testid="mission-mgmt-menu"
          >
            {canEdit && (
              <button
                onClick={() => { setOpen(false); onEdit?.(); }}
                data-testid="mission-mgmt-edit"
                className="w-full text-left px-4 py-3 text-[13px] font-medium text-[#1A1A1A] hover:bg-[#FAF7F0] flex items-center gap-2.5"
              >
                <Edit3 className="h-4 w-4 text-[#5B9EE0]" /> Modifica dettagli
              </button>
            )}
            {canLeave && (
              <button
                onClick={doLeave}
                disabled={busy}
                data-testid="mission-mgmt-leave"
                className="w-full text-left px-4 py-3 text-[13px] font-medium text-[#B75E2A] hover:bg-[#FAF7F0] flex items-center gap-2.5 disabled:opacity-60"
              >
                <LogOut className="h-4 w-4" /> Abbandona missione
              </button>
            )}
            {canCancel && (
              <button
                onClick={doCancel}
                disabled={busy}
                data-testid="mission-mgmt-cancel"
                className="w-full text-left px-4 py-3 text-[13px] font-medium text-red-600 hover:bg-red-50 flex items-center gap-2.5 disabled:opacity-60 border-t border-[#F0EBE3]"
              >
                <Trash2 className="h-4 w-4" /> Annulla missione
              </button>
            )}
            {!canEdit && !canLeave && !canCancel && (
              <div className="px-4 py-3 text-[12px] text-[#9A9A9A]">
                Nessuna azione disponibile.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
